import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

import { trackKlaviyoViewedProduct } from "@/analytics/klaviyo";
import Section from "@/blueprint/components/Section/Section";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { Product } from "@/graphql/storefront/generated";
import { type ModelVisualOrderVariant, useTrack } from "@/analytics/events";
import { useFeatureFlag } from "@/analytics/featureFlags";
import {
  type ShopPayInstallmentsTerms,
  termsForAmount,
} from "@/lib/shopPayInstallments";
import type { AddOnId } from "@/views/shop/addOns";
import {
  FLOOR_PACKAGES,
  type FloorPackageId,
} from "@/views/shop/floorPackages";
import InnovationBanner from "@/views/shop/sections/InnovationBanner";
import MaticGallery, {
  type SlideFocus,
} from "@/views/shop/sections/MaticGallery";
import Configurator from "@/views/shop/sections/Configurator";
import ConfiguratorTitle from "@/views/shop/sections/ConfiguratorTitle";
import {
  ASSURANCE_STRIP_PIN,
  SIDE_BY_SIDE_QUERY,
  VISUAL_FIRST_SECTION_LAYOUT,
} from "@/views/shop/sections/configurator/imageColumn";
import { AssuranceStrip } from "@/views/shop/sections/configurator/Assurances";
import { useActiveBlock } from "@/views/shop/sections/configurator/useActiveBlock";
import { useChromeHeights } from "@/views/shop/sections/configurator/useChromeHeights";
import ReviewWall from "@/views/shop/sections/ReviewWall";
import ShopFaqs from "@/views/shop/sections/ShopFaqs";
import SocialCarousel from "@/views/shop/sections/SocialCarousel";
import SupportCards from "@/views/shop/sections/SupportCards";
import WhatsInside from "@/views/shop/sections/WhatsInside";
import {
  benefitTab,
  playableClips,
} from "@/views/shop/sections/configurator/FeatureTabs";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import {
  CONFIGURATOR,
  CONFIGURATOR_VISUALS,
  type ConfiguratorBlockId,
  type ConfiguratorSlideId,
  type MaticModelId,
  SHOP_PAGE,
} from "@/views/shop/shopContent";
import {
  FEATURE_TABS,
  type FeatureTabId,
} from "@/views/shop/featureTabsContent";

/** The experiment picking which feature tab the zone opens on. */
const MODEL_VISUAL_ORDER_FLAG = "model-visual-order";

const isSlideId = (id: string): id is ConfiguratorSlideId =>
  id in CONFIGURATOR_VISUALS.slideLabels;

/** Add-ons with a slide of their own in the visual zone. */
const slideForAddOn = (id: AddOnId) => (isSlideId(id) ? id : null);

/**
 * Reports the active block as it changes, from inside the configurator's
 * section so the event carries it. The opening block isn't a change.
 */
const TrackActiveBlock = ({ block }: { block: ConfiguratorBlockId }) => {
  const track = useTrack();
  const isFirstRef = useRef(true);

  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      return;
    }
    track("configurator_block_active", { block_id: block });
  }, [track, block]);

  return null;
};

type ShopViewProps = {
  maticProduct?: Product | null | undefined;
  maticSereneProduct: Product | null;
  annualBagPassProduct?: Product | null | undefined;
  maticCareProduct?: Product | null | undefined;
  builderKitProduct?: Product | null | undefined;
  petBrushRollProduct?: Product | null | undefined;
  maticInstallments: ShopPayInstallmentsTerms | null;
};

const ShopView = ({
  maticProduct,
  maticSereneProduct,
  annualBagPassProduct,
  maticCareProduct,
  builderKitProduct,
  petBrushRollProduct,
  maticInstallments,
}: ShopViewProps) => {
  // The page opens on Matic, One Matic, One Dock, so the cart is ready to
  // add; a model can be switched but not cleared.
  const [modelId, setModelId] = useState<MaticModelId>("matic");
  const [floorPackageId, setFloorPackageId] =
    useState<FloorPackageId>("one-matic-one-dock");
  const [focus, setFocus] = useState<SlideFocus | null>(null);
  // The `model-visual-order` experiment, which every /product visitor is in:
  // anything but an explicit control arm, a failed load included, runs test.
  const orderFlag = useFeatureFlag(MODEL_VISUAL_ORDER_FLAG);
  const variant: ModelVisualOrderVariant =
    orderFlag.value === "control" ? "control" : "test";
  // The zone's feature tab: the shopper's pick, else Overview.
  const [chosenTab, setChosenTab] = useState<FeatureTabId | null>(null);
  const tabId: FeatureTabId = chosenTab ?? FEATURE_TABS.overview.id;
  const track = useTrack();
  const handleTabChange = (id: FeatureTabId) => {
    setChosenTab(id);
    track("visual_tab_selected", { tab_id: id, model: modelId });
  };

  // A model switch updates every tab in place without moving the shopper;
  // the quiet tab, the one that differs, is announced. Only Serene's quiet
  // tab carries the dot for a moment: it marks what Serene adds, so a
  // switch back to Matic's plain "Quiet" shows none.
  const [quietChanged, setQuietChanged] = useState(false);
  const [quietNotice, setQuietNotice] = useState("");
  const shownModelRef = useRef(modelId);
  useEffect(() => {
    const previous = shownModelRef.current;
    shownModelRef.current = modelId;
    if (previous === modelId) return;
    const model = CONFIGURATOR.models.find(({ id }) => id === modelId);
    setQuietChanged(modelId === "matic-serene");
    // Picking Serene shows what it adds: its quiet tab.
    if (modelId === "matic-serene")
      setChosenTab(FEATURE_TABS.quiet[modelId].id);
    if (model !== undefined)
      setQuietNotice(FEATURE_TABS.quietUpdated(model.title));
    const settle = window.setTimeout(
      () => setQuietChanged(false),
      FEATURE_TABS.changedMs,
    );
    return () => window.clearTimeout(settle);
  }, [modelId]);

  // A link can open the page on a pick (?model=matic-serene&package=…), as
  // the Serene CTAs do; unknown values leave the default. Set as the page's
  // own state, not a switch, so nothing is announced and the tabs stay on
  // Overview.
  const { isReady, query } = useRouter();
  useEffect(() => {
    if (!isReady) return;
    const linkedModel = CONFIGURATOR.models.find(
      ({ id }) => id === query["model"],
    );
    if (linkedModel !== undefined) {
      shownModelRef.current = linkedModel.id;
      setModelId(linkedModel.id);
    }
    const linkedPackage = FLOOR_PACKAGES.find(
      ({ id }) => id === query["package"],
    );
    if (linkedPackage !== undefined) setFloorPackageId(linkedPackage.id);
    // Read once, when the query is known: later picks are the shopper's.
  }, [isReady]);
  const shownTab = benefitTab(modelId, tabId);
  // The benefit tab's clip on screen, shared by the zone and the phone's
  // card. A new tab, or a new model's quiet tab, starts at its first clip.
  const clipKey = `${tabId}:${shownTab?.id === "quiet" ? modelId : ""}`;
  const [clip, setClip] = useState({ key: clipKey, index: 0 });
  const clipIndex = clip.key === clipKey ? clip.index : 0;
  const clipCount = shownTab === null ? 0 : playableClips(shownTab).length;
  const handleClipAdvance = () =>
    setClip((current) => ({
      key: clipKey,
      index:
        ((current.key === clipKey ? current.index : 0) + 1) %
        Math.max(clipCount, 1),
    }));
  const configuratorRef = useRef<HTMLElement>(null);
  // The options block driving the visual zone, from scroll position alone.
  const chrome = useChromeHeights();
  // The chrome (announcement and nav) scrolls away with the page, so what
  // pins under it follows its bottom edge, once a frame, written straight
  // to the style rather than through state so scrolling never re-renders.
  const chromeEdgeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = chromeEdgeRef.current;
    if (element === null || chrome === null) return;
    const total = chrome.banner + chrome.header;
    let frame = 0;
    const update = () => {
      frame = 0;
      element.style.setProperty(
        "--zone-top-side",
        `${Math.max(0, total - window.scrollY)}px`,
      );
    };
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    return () => {
      window.removeEventListener("scroll", schedule);
      cancelAnimationFrame(frame);
    };
  }, [chrome]);
  const activeBlock = useActiveBlock(configuratorRef);

  const focusSlide = (slideId: ConfiguratorSlideId) =>
    setFocus((previous) => ({ slideId, key: (previous?.key ?? 0) + 1 }));

  // From `sd`, an add-on switched on, or opened to read, takes the zone to
  // its imagery; those without any leave the zone as it is. Stacked, the
  // zone stays on the package and the details dialog shows the add-on.
  const sideBySide = useMediaQuery(SIDE_BY_SIDE_QUERY) === true;
  const handleAddOnToggle = (id: AddOnId, isAdding: boolean) => {
    const slideId = slideForAddOn(id);
    if (sideBySide && isAdding && slideId !== null) focusSlide(slideId);
  };
  const handleAddOnFocus = (id: AddOnId) => {
    const slideId = slideForAddOn(id);
    if (sideBySide && slideId !== null) focusSlide(slideId);
  };

  // From `sd`, a package picked shows the renders counting its robots,
  // wherever the scroll has the zone; stacked, the zone keeps the films.
  const handlePackageSelect = (id: FloorPackageId) => {
    setFloorPackageId(id);
    if (sideBySide) focusSlide("setup");
  };

  // Shopify's terms quote the price they were read for (the live store's);
  // the title's line quotes the Matic price this page shows instead, as the
  // sticky bar quotes its cart, so the two always agree.
  const maticPrice = maticProduct?.variants.edges[0]?.node.price ?? null;
  const titleInstallments =
    maticInstallments === null || maticPrice === null
      ? maticInstallments
      : (termsForAmount(
          maticInstallments,
          Number(maticPrice.amount),
          maticPrice.currencyCode,
        ) ?? maticInstallments);

  useEffect(() => {
    if (maticProduct != null) {
      trackKlaviyoViewedProduct(maticProduct, "Robot");
    }
  }, [maticProduct]);
  return (
    <div
      ref={chromeEdgeRef}
      className="flex flex-col"
      // Where the visual zone pins beside the options: under the nav and
      // the announcement while they're on screen (IMAGE_COLUMN_FRAME). The
      // nav scrolls away, so the effect above follows its bottom edge up to
      // the top of the screen, and the zone grows into the room it leaves.
      style={
        chrome === null
          ? undefined
          : ({
              "--zone-top-side": `${chrome.banner + chrome.header}px`,
            } as CSSProperties)
      }
    >
      <Section
        ref={configuratorRef}
        id="configurator"
        position={1}
        className={VISUAL_FIRST_SECTION_LAYOUT}
      >
        <TrackActiveBlock block={activeBlock} />
        <div
          data-config-block="title"
          // On load the title, rating and installments rise in, 40ms apart,
          // once; reduced motion shows them as they are.
          className="w-full sd:col-start-2 sd:row-start-1 sd:pt-4xl [&>div>*:nth-child(2)]:[animation-delay:40ms] [&>div>*:nth-child(3)]:[animation-delay:80ms] [&>div>*]:animate-rise-in [&>div>*]:motion-reduce:animate-none"
        >
          <ConfiguratorTitle installments={titleInstallments} />
        </div>
        <AssuranceStrip className={ASSURANCE_STRIP_PIN} />
        <VisuallyHidden>
          <h1>{SHOP_PAGE.title}</h1>
        </VisuallyHidden>
        <MaticGallery
          activeBlock={activeBlock}
          modelId={modelId}
          floorPackageId={floorPackageId}
          petBrushRollImageUrl={
            petBrushRollProduct?.images.edges[0]?.node.url ?? null
          }
          focus={focus}
          tabId={tabId}
          onTabChange={handleTabChange}
          clipIndex={clipIndex}
          onClipAdvance={handleClipAdvance}
          quietChanged={quietChanged}
          variant={variant}
          className="order-first sd:order-none sd:col-start-1 sd:row-span-2 sd:row-start-1"
        />
        <VisuallyHidden>
          <span aria-live="polite">{quietNotice}</span>
        </VisuallyHidden>
        <Configurator
          selectedModelId={modelId}
          installments={maticInstallments}
          onSelectModel={setModelId}
          selectedId={floorPackageId}
          onSelect={handlePackageSelect}
          onAddOnToggle={handleAddOnToggle}
          onAddOnFocus={handleAddOnFocus}
          maticProduct={maticProduct}
          maticSereneProduct={maticSereneProduct}
          annualBagPassProduct={annualBagPassProduct}
          maticCareProduct={maticCareProduct}
          builderKitProduct={builderKitProduct}
          petBrushRollProduct={petBrushRollProduct}
          className="sd:col-start-2 sd:row-start-2"
        />
      </Section>
      <WhatsInside position={2} />
      <ReviewWall position={3} />
      <InnovationBanner position={4} />
      <ShopFaqs position={5} />
      <SupportCards position={6} />
      <SocialCarousel position={7} />
    </div>
  );
};

export default ShopView;
